import { ArcElement, Chart as ChartJS, Legend, SubTitle, Title, Tooltip } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment/min/moment-with-locales";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Modal, Row, Stack, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { FiletypePng } from "react-bootstrap-icons";
import { Chart } from "react-chartjs-2";
import { useRouteLoaderData } from "react-router-dom";
import ErrorResponse from "./ErrorResponse";
import { formatDemographicText, toAutoDecimals, formatDaypartText } from "./HelperFunctions";
import { useFetchData, useRadioName } from "./HelperHooks";
import LoadingIndicator from "./LoadingIndicator";
import { backgroundPlugin } from "./chartOptions";

ChartJS.register(ArcElement, Legend, Tooltip, ChartDataLabels, Title, SubTitle);

export default function ContentGroupTrend({
    show,
    onHide,
    radioId,
    contentGroupId,
    keywords = "",
    dataType,
    demoId = 1,
    monthsLimit = 8,
    contentGroups,
    selectedContentGroupIds = [],
    filtered = false,
    itemTypes = [],
    includeHidden = false,
    includeInvalid = false,
    daypart = null,
    days = [],
    start_time = null,
    end_time = null,
}) {
    const { radios, demographics } = useRouteLoaderData("root");
    const [loading, errorCode, data, fetchData] = useFetchData();
    const radioName = useRadioName();
    const [currentRadioName, setCurrentRadioName] = useState("");
    const [grouping, setGrouping] = useState(filtered ? "week" : "day");
    const [demoName, setDemoName] = useState("");
    const [daypartName, setDaypartName] = useState("");
    const chartRef = useRef(null);
    const [timeRange, setTimeRange] = useState("3months");
    const [percentageGrouping, setPecentageGrouping] = useState("breaks");

    useEffect(() => {
        const fetchRadioName = async () => {
            try {
                const radioName = (await radios).data.find(x => x.radio_id === radioId)?.radio_name;
                if (radioName) {
                    setCurrentRadioName(radioName);
                }
            } catch (error) {
                console.error("Error fetching radio name:", error);
            }
        };

        fetchRadioName();
    }, [radioId, radios]);

    useEffect(() => {
        async function getDaypartName() {
            setDaypartName(formatDaypartText(daypart));
        }
        if (daypart) getDaypartName();
    }, [dataType, daypart, radioId]);

    useEffect(() => {
        async function loadData() {
            const input = {
                radio_id: radioId,
                data_type: dataType,
                demo_id: demoId,
            };

            if (contentGroupId) {
                input.content_groups = [contentGroupId];
                input.months_limit = monthsLimit;
                input.daypart_id = daypart?.daypart_id ?? null;
                input.days = days;
                input.start_time = start_time;
                input.end_time = end_time;
                await fetchData("content_testing/trend", input);
            } else if (filtered) {
                input.keywords = keywords;
                input.content_groups = selectedContentGroupIds;
                input.include_hidden = includeHidden;
                input.exclude_invalid = includeInvalid;

                if (timeRange === "3months") {
                    input.months_limit = 3;
                }
                await fetchData("content_testing/trend", input);
            }
            console.log(data);
        }
        if (filtered) {
            loadData();
        } else {
            if (radioId && (contentGroupId || (keywords && keywords.trim().length > 0))) {
                loadData();
            }
        }
    }, [contentGroupId, keywords, dataType, demoId, fetchData, monthsLimit, radioId, timeRange]);

    useEffect(() => {
        async function getDemoName() {
            const demo = (await demographics).data.find(x => x.demo_id === demoId);
            setDemoName(formatDemographicText(demo));
        }
        if (demoId) getDemoName();
    }, [demoId, demographics]);

    const chartTitle = useMemo(() => {
        if (contentGroupId) {
            return contentGroups.find(x => x.group_id === contentGroupId)?.display_name;
        } else if (filtered) {
            return selectedContentGroupIds.length || keywords
                ? `Filtered trend for ${keywords ? `'${keywords}'` : ""}${keywords && selectedContentGroupIds.length ? " in " : ""}${selectedContentGroupIds
                      .map(id => contentGroups.find(x => x.group_id === id)?.display_name)
                      .filter(Boolean)
                      .join(", ")}`
                : "Filtered trend";
        }
        return "Trend Data";
    }, [contentGroupId, contentGroups, keywords]);

    const hasScores = useMemo(() => data?.data_week?.filter(x => x.score > 0)?.length, [data?.data_week]);

    const percentageKey = percentageGrouping === "breaks" ? "percentage_breaks" : "percentage_duration";

    const chartOptions = {
        aspectRatio: window.innerHeight - 100 < window.innerWidth || window.innerWidth > 800 ? 2 : 1,
        spanGaps: true,
        plugins: {
            title: {
                display: true,
                text: chartTitle,
                font: { size: 18 },
                padding: { bottom: 5 },
            },
            subtitle: {
                display: true,
                text: `${currentRadioName} |  ${filtered ? daypartName + " |" : ""} ${demoName} ${monthsLimit ? `| Latest ${monthsLimit} months` : ""}`,
            },
            datalabels: {
                color: "#142533",
                font: { weight: "bold" },
            },
            legend: {
                position: "bottom",
                labels: { usePointStyle: true },
                reverse: true,
            },
            customCanvasBackgroundColor: { color: "white" },
            tooltip: { enabled: false },
        },
        scales: {
            percentage: {
                position: "right",
                type: "linear",
                // ticks: {
                //     callback: function (value) {
                //         const dur = moment.duration(value || 0, "seconds");
                //         const hours = Math.floor(dur.asHours());
                //         const mins = Math.floor(dur.minutes());
                //         if (hours > 0) return `${hours}h:${String(mins).padStart(2, "0")}m`;
                //         else return `${mins} mins`;
                //     },
                // },
                beginAtZero: true,
                grid: { display: false },
                border: { display: false },
            },
            scores: {
                type: "linear",
                max: 10,
                position: "left",
                display: hasScores,
                beginAtZero: true,
                grid: { display: false },
                border: { display: false },
                offset: true,
            },
            x: {
                grid: { display: false },
                ticks: { maxRotation: 90 },
            },
        },
        interaction: { mode: "index" },
    };

    const chartData = useMemo(() => {
        if (data) {
            const selectedGroupingData = data[`data_${grouping}`].filter(x => x[percentageKey] > 0);
            const scoreDataset = {
                type: "line",
                label: "Score",
                borderColor: "#7BA7CC",
                pointBackgroundColor: "#7BA7CC",
                borderWidth: 4,
                pointBorderWidth: 1,
                pointRadius: 6,
                pointHoverRadius: 6,
                data: selectedGroupingData.map(x => toAutoDecimals(x.score, 1)),
                yAxisID: "scores",
                datalabels: {
                    labels: {
                        index: {
                            align: "end",
                            anchor: "end",
                        },
                    },
                },
            };
            const percentageDataset = {
                type: "bar",
                backgroundColor: "#fcd76a",
                label: percentageGrouping === "breaks" ? "Breaks" : " Duration",
                yAxisID: "percentage",
                borderRadius: 5,
                pointStyle: "rectRounded",
                data: selectedGroupingData.map(x => x[percentageKey]),
                datalabels: {
                    formatter: function (value) {
                        return value ? `${Math.round(value)}%` : `${toAutoDecimals(value, 1, "")}`;
                    },
                    labels: {
                        index: {
                            align: "end",
                            anchor: "start",
                        },
                    },
                },
            };

            return {
                labels: selectedGroupingData.map(x => moment(x.start_datetime).format("L")),
                datasets: hasScores ? [scoreDataset, percentageDataset] : [percentageDataset],
            };
        }
    }, [data, grouping, hasScores, percentageGrouping]);

    async function exportPng() {
        const base64Image = chartRef.current.toBase64Image();
        const link = document.createElement("a");
        if (filtered) {
            link.download = `Filtered_Trend_${currentRadioName}.png`.replaceAll(" ", "_");
        } else {
            link.download = `Trend_${contentGroups.find(x => x.group_id === contentGroupId)?.display_name}_${currentRadioName}.png`.replaceAll(" ", "_");
        }
        link.href = base64Image;
        link.click();
    }

    return (
        <Modal show={show} onHide={onHide} size="xl" centered className="trend-over-time-modal">
            <Modal.Header closeButton className=" py-1 py-xl-3">
                <Modal.Title className=" text-uppercase">Content Group Trend</Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column pt-1 row-gap-2">
                <Stack direction="horizontal" gap={2} className=" flex-wrap">
                    <span className="d-none d-md-inline-block fw-bold text-uppercase fs-5">Group By:</span>
                    <ToggleButtonGroup type="radio" name="tot-grouping" className="ra-switcher" onChange={val => setGrouping(val)} value={grouping}>
                        <ToggleButton id={`trend-day`} value={"day"} variant="ra-dark-blue-outline" className=" align-content-center">
                            Day
                        </ToggleButton>
                        <ToggleButton id={`trend-week`} value={"week"} variant="ra-dark-blue-outline" className=" align-content-center">
                            Week
                        </ToggleButton>
                        <ToggleButton id={`trend-month`} value={"month"} variant="ra-dark-blue-outline" className="align-items-center column-gap-1">
                            Month
                        </ToggleButton>
                    </ToggleButtonGroup>
                    {filtered ? (
                        <>
                            <span className="d-none d-md-inline-block fw-bold text-uppercase fs-5">Limit:</span>
                            <ToggleButtonGroup
                                type="radio"
                                name="time-range-picker"
                                value={timeRange}
                                onChange={val => setTimeRange(val)}
                                className="ra-switcher">
                                <ToggleButton id="time-3months" value="3months" variant="ra-dark-blue-outline" className="align-content-center">
                                    3 Months
                                </ToggleButton>
                                <ToggleButton id="time-alltime" value="alltime" variant="ra-dark-blue-outline" className="align-content-center text-nowrap">
                                    All Time
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </>
                    ) : null}

                    <Col xs={12} md="auto">
                        <ToggleButtonGroup
                            type="radio"
                            name="tot-grouping-percentage"
                            className="ra-switcher w-100"
                            onChange={val => setPecentageGrouping(val)}
                            value={percentageGrouping}>
                            <ToggleButton id="percentage-breaks-tot" value="breaks" variant="ra-dark-blue-outline" className="px-1">
                                % Breaks
                            </ToggleButton>
                            <ToggleButton id="percentage-duration-tot" value="duration" variant="ra-dark-blue-outline" className="px-1">
                                % Duration
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Col>
                    <Button
                        variant={"contained"}
                        onClick={exportPng}
                        disabled={!data}
                        className="p-0 border-0 btn-download ms-auto d-flex align-items-center column-gap-2">
                        <FiletypePng width={35} height={35} />
                        <span className="d-none d-md-inline-block">Screenshot</span>
                    </Button>
                </Stack>

                <div>
                    {loading && <LoadingIndicator width={75} />}
                    {!loading && errorCode && <ErrorResponse errorCode={errorCode} />}
                    {data && <Chart ref={chartRef} data={chartData} options={chartOptions} plugins={[backgroundPlugin]} />}
                </div>
            </Modal.Body>
        </Modal>
    );
}
